import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  computed,
  Renderer2,
  ViewChild,
  ElementRef,
  ViewChildren,
  AfterViewInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { map } from 'rxjs';
import { ContentBlock } from 'src/app/content-blocks/models';
import { toSignal } from '@angular/core/rxjs-interop';
import { JsonPipe, NgClass } from '@angular/common';
import { ContentBlockDefaultComponent } from 'src/app/content-blocks/components/content-block-default/content-block-default.component';
import { ContentBlockBaseComponent } from 'src/app/content-blocks/components/content-block-base/content-block-base.component';
import { ContentBlockAgendaComponent } from 'src/app/content-blocks/components/content-block-agenda/content-block-agenda.component';
import { getConsecutiveBlockTypes } from 'src/app/content-blocks/utils/consecutive-blocktypes';
import { ContentBlockLargeMediaComponent } from 'src/app/content-blocks/components/content-block-large-media/content-block-large-media.component';
import { ContentBlockCtaComponent } from 'src/app/content-blocks/components/content-block-cta/content-block-cta.component';
import { ContentBlockCtaCardsComponent } from 'src/app/content-blocks/components/content-block-cta-cards/content-block-cta-cards.component';
import { ContentBlockWysiwygComponent } from 'src/app/content-blocks/components/content-block-wysiwyg/content-block-wysiwyg.component';
import { ContentBlockAnchorNavComponent } from 'src/app/content-blocks/components/content-block-anchor-nav/content-block-anchor-nav.component';
import { TextUtilityModule } from '@teamfoster/sdk/text-utility';
import { ActivatedRoute, Route } from '@angular/router';
import { ContentBlockNewsComponent } from 'src/app/content-blocks/components/content-block-news/content-block-news.component';
import { ContentBlocklocationsComponent } from 'src/app/content-blocks/components/content-block-locations/content-block-locations.component';
import { ContentBlockFaqComponent } from 'src/app/content-blocks/components/content-block-faq/content-block-faq.component';
import { ContentBlockMapComponent } from 'src/app/content-blocks/components/content-block-map/content-block-map.component';
import { ContentBlockCollectionsComponent } from 'src/app/content-blocks/components/content-block-collections/content-block-collections.component';
import { Header } from 'src/app/standalone/models/header.model';
import { ContentBlockNewsletterComponent } from 'src/app/content-blocks/components/content-block-newsletter/content-block-newsletter.component';
import { ContentBlockCtaImageCardsComponent } from 'src/app/content-blocks/components/content-block-cta-image-cards/content-block-cta-image-cards.component';

@Component({
  selector: 'app-content-block-container',
  templateUrl: './content-block-container.component.html',
  styleUrls: ['./content-block-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    ContentBlockDefaultComponent,
    ContentBlockBaseComponent,
    ContentBlockLargeMediaComponent,
    ContentBlockCtaComponent,
    ContentBlockCtaCardsComponent,
    ContentBlockWysiwygComponent,
    ContentBlockAgendaComponent,
    ContentBlockCtaCardsComponent,
    JsonPipe,
    ContentBlockAnchorNavComponent,
    TextUtilityModule,
    ContentBlockNewsComponent,
    ContentBlocklocationsComponent,
    ContentBlockFaqComponent,
    ContentBlockMapComponent,
    ContentBlockCollectionsComponent,
    ContentBlockNewsletterComponent,
    ContentBlockCtaImageCardsComponent,
  ],
})
export class ContentBlockContainerComponent {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  @ViewChild('content') contentRef?: ElementRef;
  @ViewChildren(ContentBlockBaseComponent) contentBlocks?: ContentBlockBaseComponent[];

  blocks = input.required<ContentBlock[]>();
  header = input<Header>();
  debug = input<boolean>(false);
  showLabels = input<boolean>(false);
  showAnchors = input<boolean>(false);

  anchorBlocks = computed(() => {
    return this.blocksListInfo$().filter(block => block.blockType !== 'block-anchor-nav' && !!block.anchorTitle);
  });

  headerHasInsetOption = computed(() => {
    const header = this.header();
    if (header?.type === 'header-standard') {
      return true;
    }
    if (header?.type === '' && !header.image?.url) {
      return true;
    }
    return false;
  });

  // filter empty blocks
  blocksWithContent = computed(() => {
    const allBlocks = this.blocks();
    const blocks: ContentBlock[] = allBlocks.filter(block => {
      if (block.blockType === 'block-anchor-nav') {
        return allBlocks.filter(block => block.blockType !== 'block-anchor-nav' && !!block.menuTitle).length > 0;
      }
      if (block.blockType === 'block-cta-cards') {
        return block?.ctaItems?.length || 0 > 0;
      }
      if (block.blockType === 'block-event') {
        return block?.events?.length || 0 > 0;
      }
      if (block.blockType === 'block-news-featured') {
        return block?.news?.length || 0 > 0;
      }
      return true;
    });

    return blocks;
  });

  cookiePreferences$ = toSignal(
    this.store.select(getCookiePreferences).pipe(
      map(
        a =>
          a || {
            functional: false,
            analytical: false,
            marketing: false,
          }
      )
    )
  );

  blocksListInfo$ = computed(() => getConsecutiveBlockTypes(this.blocksWithContent()));

  sectionSpacing(blockType: string) {
    const excludeBlockTypes = ['block-anchor-nav', 'block-event'];
    return !excludeBlockTypes.includes(blockType) ? 'section-outset-y' : '';
  }

  copyToClipboard(text: string) {
    if (text) {
      navigator.clipboard
        .writeText(location.pathname + '#' + text)
        .then(a => {
          alert(`${location.pathname}#${text} is gekopieerd naar klembord`);
        })
        .catch();
    }
  }
}
